import { useCallback, useRef, useState } from 'react';
import { VectorTile } from '@ais-ui/websockets';
import { VectorTile as VectorTileLayer } from 'ol/layer';
import { VectorTile as VectorTileSource } from 'ol/source';
import MVT from 'ol/format/MVT';
import { Stroke, Style } from 'ol/style';
import { FeatureLike } from 'ol/Feature';
import { LayerItem } from '../layerManagement/types';

export interface HoverState {
  feature: FeatureLike | null;
  position: { x: number; y: number } | null;
}

interface VectorTileHook {
  addVectorTile: (
    vectorTile: VectorTile,
    hover: boolean,
    id?: string,
  ) => LayerItem;
  vectorTileHoverState: HoverState;
  onVectorTileHover: (hoverState: HoverState) => void;
}

export function useVectorTiles(
  addLayer: (
    layer: any,
    name: string,
    isVisible: boolean,
    isBase: boolean,
    canShowLabel: boolean,
    properties?: any,
    id?: string,
  ) => LayerItem,
): VectorTileHook {
  const [vectorTileHoverState, setVectorTileHoverState] = useState<HoverState>({
    feature: null,
    position: null,
  });

  // Style definitions
  const styles = useRef({
    default: new Style({
      stroke: new Stroke({
        color: '#ffcc33',
        width: 3,
      }),
    }),
    focus: new Style({
      stroke: new Stroke({
        color: '#00ff00',
        width: 8,
      }),
    }),
  }).current;

  const getVectorTileLayerStyle = useCallback(
    (feature: FeatureLike) => {
      // Check if this is the currently hovered feature
      if (vectorTileHoverState.feature === feature) {
        return new Style({
          stroke: new Stroke({
            color: '#00ff00', // Highlight color
            width: 6, // Wider stroke for hover state
          }),
        });
      }

      // Default style
      return new Style({
        stroke: new Stroke({
          color: '#ffcc33',
          width: 3,
        }),
      });
    },
    [vectorTileHoverState],
  );

  const addVectorTile = useCallback(
    (vectorTile: VectorTile, hover: boolean, id?: string): LayerItem => {
      const layer = new VectorTileLayer({
        source: new VectorTileSource({
          format: new MVT(),
          url: `${vectorTile.url}/{z}/{x}/{y}.pbf`,
          maxZoom: 14,
          tileSize: 512,
        }),
        style: getVectorTileLayerStyle,
        updateWhileAnimating: false,
        updateWhileInteracting: false,
        preload: 1,
      });

      return addLayer(
        layer,
        vectorTile.name,
        true,
        false,
        false,
        { hoverable: hover },
        id,
      );
    },
    [addLayer, getVectorTileLayerStyle],
  );

  const onVectorTileHover = useCallback((hoverState: HoverState) => {
    setVectorTileHoverState(hoverState);
  }, []);

  return {
    addVectorTile,
    vectorTileHoverState,
    onVectorTileHover,
  };
}
